// AI service for calendar event management
import { createClient } from '@blinkdotnew/sdk';
import { calendarService } from './calendarService';
import type { CalendarEvent, CalendarEventInput } from '../types/calendar';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export class CalendarAIService {
  // Retry with exponential backoff
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    initialDelay: number = 1000
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;
        
        // Check if it's a rate limit error
        const isRateLimit = 
          error?.message?.includes('Rate limit exceeded') || 
          error?.message?.includes('RATE_LIMITED') ||
          error?.code === 'NETWORK_ERROR' || 
          error?.status === 429;
        
        if (!isRateLimit || attempt === maxRetries - 1) {
          throw error;
        }
        
        // Wait with exponential backoff
        const delay = initialDelay * Math.pow(2, attempt);
        console.log(`Rate limited - retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }

  // Categorize event using AI with rate limit handling
  async categorizeEvent(event: CalendarEvent): Promise<string> {
    try {
      return await this.retryWithBackoff(async () => {
        const { text } = await blink.ai.generateText({
          prompt: `Categorize this calendar event into a single category:\n\nTitle: ${event.title}\nDescription: ${event.description || 'N/A'}\nType: ${event.eventType}\nLocation: ${event.location || 'N/A'}\n\nCommon categories: Work, Personal, Health, Education, Social, Finance, Travel, Shopping, Entertainment, Family\n\nReturn only the category name, nothing else.`,
          maxTokens: 20
        });
        
        return text.trim();
      });
    } catch (error: any) {
      // Check if it's a rate limit error after retries exhausted
      if (error?.message?.includes('Rate limit exceeded') || error?.message?.includes('RATE_LIMITED') || error?.code === 'NETWORK_ERROR' || error?.status === 429) {
        console.log('AI rate limit reached after retries - categorization skipped');
        return 'Uncategorized';
      }
      console.error('Error categorizing event:', error);
      return 'Uncategorized';
    }
  }

  // Find related emails for an event (integrated with email index) with rate limit handling
  async findRelatedEmails(event: CalendarEvent): Promise<string[]> {
    try {
      // Build search query from event details
      const searchTerms = [
        event.title,
        ...(event.attendees || []),
        event.location
      ].filter(Boolean);

      if (searchTerms.length === 0) {
        return [];
      }

      // Use AI to search emails intelligently
      const { text } = await blink.ai.generateText({
        prompt: `Given this calendar event:
- Title: ${event.title}
- Description: ${event.description || 'N/A'}
- Attendees: ${event.attendees?.join(', ') || 'None'}
- Location: ${event.location || 'N/A'}
- Time: ${event.startTime.toLocaleString()}

Generate 3-5 search queries that would find related emails in Gmail/Outlook. Focus on:
- Subject line keywords
- Sender/recipient email addresses
- Meeting links or location names
- Project/topic keywords

Return only the search queries, one per line.`,
        maxTokens: 200,
        search: true
      });

      // Parse search queries
      const queries = text.trim().split('\n').filter(q => q.trim());
      
      // For now, return the search queries as email IDs (placeholder)
      // In production, these would be actual email IDs from Gmail/Outlook API
      // Format: query:base64(query) to simulate email IDs
      return queries.map(q => `query:${btoa(q.trim())}`);
      
    } catch (error: any) {
      // Check if it's a rate limit error
      if (error?.message?.includes('Rate limit exceeded') || error?.code === 'NETWORK_ERROR' || error?.status === 429) {
        console.log('AI rate limit reached - email linking will be retried later');
        throw new Error('RATE_LIMITED');
      }
      console.error('Error finding related emails:', error);
      return [];
    }
  }

  // Generate AI summary for an event with rate limit handling
  async generateEventSummary(event: CalendarEvent): Promise<string> {
    try {
      const { text } = await blink.ai.generateText({
        prompt: `Create a brief, helpful summary for this calendar event:\n\nTitle: ${event.title}\nDescription: ${event.description || 'N/A'}\nType: ${event.eventType}\nTime: ${event.startTime.toLocaleString()} - ${event.endTime.toLocaleString()}\nLocation: ${event.location || 'N/A'}\nAttendees: ${event.attendees?.join(', ') || 'None'}\n\nGenerate a concise 1-2 sentence summary that captures the key information.`,
        maxTokens: 100
      });
      
      return text.trim();
    } catch (error: any) {
      // Check if it's a rate limit error
      if (error?.message?.includes('Rate limit exceeded') || error?.code === 'NETWORK_ERROR' || error?.status === 429) {
        console.log('AI rate limit reached - summary generation will be retried later');
        throw new Error('RATE_LIMITED');
      }
      console.error('Error generating event summary:', error);
      return '';
    }
  }

  // Batch categorize events with rate limit handling
  async categorizeEvents(events: CalendarEvent[]): Promise<Map<string, string>> {
    const categories = new Map<string, string>();
    const uncategorized = events.filter(e => !e.category);
    
    // Process in batches to avoid overwhelming the API
    const batchSize = 3;
    const delayBetweenBatches = 500; // 500ms delay between batches
    
    for (let i = 0; i < uncategorized.length; i += batchSize) {
      const batch = uncategorized.slice(i, i + batchSize);
      
      // Process batch in parallel
      const results = await Promise.allSettled(
        batch.map(event => this.categorizeEvent(event))
      );
      
      // Collect successful results
      results.forEach((result, index) => {
        const event = batch[index];
        if (result.status === 'fulfilled') {
          categories.set(event.id, result.value);
        } else {
          console.log(`Failed to categorize event ${event.id}:`, result.reason);
          categories.set(event.id, 'Uncategorized');
        }
      });
      
      // Add delay between batches to respect rate limits
      if (i + batchSize < uncategorized.length) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }
    
    return categories;
  }

  // Parse natural language to create calendar events
  async createEventFromNaturalLanguage(userId: string, input: string): Promise<CalendarEvent> {
    try {
      const { object } = await blink.ai.generateObject({
        prompt: `Parse this natural language input into a calendar event:
"${input}"

Extract:
- Event title (required)
- Description (optional)
- Start time (convert to ISO format, use today's date if not specified)
- End time (if not specified, make it 1 hour after start)
- Event type (meeting, reminder, task, or event)
- Location (if mentioned)
- Attendees (email addresses if mentioned)
- Whether it's all day (true/false)

Current date and time for reference: ${new Date().toISOString()}`,
        schema: {
          type: 'object',
          properties: {
            title: { type: 'string' },
            description: { type: 'string' },
            startTime: { type: 'string' },
            endTime: { type: 'string' },
            eventType: { type: 'string', enum: ['meeting', 'reminder', 'task', 'event'] },
            location: { type: 'string' },
            attendees: { type: 'array', items: { type: 'string' } },
            isAllDay: { type: 'boolean' }
          },
          required: ['title', 'startTime', 'endTime']
        }
      });

      const eventData = object as any;
      
      return await calendarService.createEvent(userId, {
        title: eventData.title,
        description: eventData.description,
        startTime: new Date(eventData.startTime),
        endTime: new Date(eventData.endTime),
        eventType: eventData.eventType || 'event',
        location: eventData.location,
        attendees: eventData.attendees,
        isAllDay: eventData.isAllDay || false
      });
    } catch (error) {
      console.error('Error creating event from natural language:', error);
      throw new Error('Failed to parse event details from input');
    }
  }

  // Update event using natural language
  async updateEventFromNaturalLanguage(eventId: string, input: string): Promise<CalendarEvent | null> {
    try {
      // First get the existing event
      const existingEvent = await calendarService.getEvent(eventId);
      
      if (!existingEvent) {
        console.warn(`Event ${eventId} not found`);
        return null;
      }

      const { object } = await blink.ai.generateObject({
        prompt: `Update this calendar event based on the user's request:

Current event:
- Title: ${existingEvent.title}
- Description: ${existingEvent.description || 'None'}
- Start: ${existingEvent.startTime.toISOString()}
- End: ${existingEvent.endTime.toISOString()}
- Type: ${existingEvent.eventType}
- Location: ${existingEvent.location || 'None'}

User's update request: "${input}"

Return only the fields that should be updated. If a field shouldn't change, don't include it.
Current date/time for reference: ${new Date().toISOString()}`,
        schema: {
          type: 'object',
          properties: {
            title: { type: 'string' },
            description: { type: 'string' },
            startTime: { type: 'string' },
            endTime: { type: 'string' },
            eventType: { type: 'string', enum: ['meeting', 'reminder', 'task', 'event'] },
            location: { type: 'string' },
            attendees: { type: 'array', items: { type: 'string' } },
            isAllDay: { type: 'boolean' }
          }
        }
      });

      const updates = object as any;
      const updateData: Partial<CalendarEventInput> = {};

      if (updates.title) updateData.title = updates.title;
      if (updates.description) updateData.description = updates.description;
      if (updates.startTime) updateData.startTime = new Date(updates.startTime);
      if (updates.endTime) updateData.endTime = new Date(updates.endTime);
      if (updates.eventType) updateData.eventType = updates.eventType;
      if (updates.location) updateData.location = updates.location;
      if (updates.attendees) updateData.attendees = updates.attendees;
      if (updates.isAllDay !== undefined) updateData.isAllDay = updates.isAllDay;

      return await calendarService.updateEvent(eventId, updateData);
    } catch (error) {
      console.error('Error updating event from natural language:', error);
      throw new Error('Failed to update event');
    }
  }

  // Find events using natural language query
  async findEvents(userId: string, query: string): Promise<CalendarEvent[]> {
    try {
      // Get all user events
      const allEvents = await calendarService.getUserEvents(userId);

      if (allEvents.length === 0) {
        return [];
      }

      // Use AI to filter relevant events
      const { object } = await blink.ai.generateObject({
        prompt: `Given this query: "${query}"
        
And these calendar events:
${allEvents.map((e, i) => `${i}. ${e.title} - ${e.startTime.toISOString()} (${e.eventType})`).join('\n')}

Return the indices of events that match the query. Consider:
- Time references (today, tomorrow, this week, next month, etc.)
- Event type (meetings, reminders, tasks)
- Event title keywords
- Date ranges

Current date/time: ${new Date().toISOString()}`,
        schema: {
          type: 'object',
          properties: {
            matchingIndices: { type: 'array', items: { type: 'number' } }
          },
          required: ['matchingIndices']
        }
      });

      const result = object as any;
      const indices = result.matchingIndices || [];
      
      return indices
        .filter((i: number) => i >= 0 && i < allEvents.length)
        .map((i: number) => allEvents[i]);
    } catch (error) {
      console.error('Error finding events:', error);
      return [];
    }
  }

  // Generate smart suggestions for scheduling
  async suggestEventTime(userId: string, eventTitle: string, duration: number = 60): Promise<{
    suggestedStart: Date;
    suggestedEnd: Date;
    reason: string;
  }> {
    try {
      // Get upcoming events
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const upcomingEvents = await calendarService.getEventsInRange(userId, now, nextWeek);

      const { object } = await blink.ai.generateObject({
        prompt: `Suggest a good time for this event: "${eventTitle}"
Duration: ${duration} minutes

Existing events in the next 7 days:
${upcomingEvents.map(e => `- ${e.title}: ${e.startTime.toISOString()} to ${e.endTime.toISOString()}`).join('\n')}

Current date/time: ${now.toISOString()}

Find a gap in the schedule that:
- Avoids conflicts
- Is during typical working hours (9 AM - 5 PM on weekdays)
- Provides buffer time between events
- Is as soon as reasonably possible`,
        schema: {
          type: 'object',
          properties: {
            suggestedStart: { type: 'string' },
            suggestedEnd: { type: 'string' },
            reason: { type: 'string' }
          },
          required: ['suggestedStart', 'suggestedEnd', 'reason']
        }
      });

      const suggestion = object as any;
      
      return {
        suggestedStart: new Date(suggestion.suggestedStart),
        suggestedEnd: new Date(suggestion.suggestedEnd),
        reason: suggestion.reason
      };
    } catch (error) {
      console.error('Error suggesting event time:', error);
      // Fallback: suggest tomorrow at 10 AM
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(10, 0, 0, 0);
      
      const endTime = new Date(tomorrow);
      endTime.setMinutes(endTime.getMinutes() + duration);

      return {
        suggestedStart: tomorrow,
        suggestedEnd: endTime,
        reason: 'Default suggestion: tomorrow at 10 AM'
      };
    }
  }
}

export const calendarAI = new CalendarAIService();
